<!-- eslint-disable no-unused-vars -->
<script setup>
import { ref, reactive, computed, inject, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import isEqual from 'lodash/isEqual';

import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'

import { 
  ClipboardDocumentListIcon, 
  AcademicCapIcon, 
  GiftIcon, 
  InformationCircleIcon, 
  BookOpenIcon,
  FireIcon
} from '@heroicons/vue/24/outline';

import { useClassStore } from '@/stores/ClassStore'
import { useUserStore } from '@/stores/UserStore'
import { useCartStore } from '@/stores/CartStore'
import { useInstructorStore } from '@/stores/InstructorStore'
import { useTitle } from '../../hooks/useTitle'
import LoadingHolder from '@/components/LoadingHolder.vue'
import DateFilter from './DateFilter.vue'
import ClassFilters from './ClassFilters.vue'

const siteConfig = inject('siteConfig')

const classStore = useClassStore()
const userStore = useUserStore()
const cartStore = useCartStore()
const instructorStore = useInstructorStore()

const router = useRouter()
const route = useRoute()

const payBalance = () => {
  cartStore.addAccountPayment({
    amount: userStore.balance,
    accountItems: userStore.accountItems,
  })
  setTimeout(() => {
    cartStore.toggleCart(true)
  }, 50)
}

const learnMoreLabel = (cls) => {
  if (cls && cls.displayOnly || !classStore.userInWhitelist(cls)) {
    if (siteConfig.copy?.classes?.learnMoreLabelForDisplayOnlyClasses) {
      return siteConfig.copy.classes.learnMoreLabelForDisplayOnlyClasses
    }
  } else {
    let ok = true
    ok = ok && !cls.daySales // XXX hack
    ok = ok && cls.classIsFull // (classDetails.seatsAvailable <= 0)
    ok = ok && cls.waitList

    if (cls.membersOnly) {
      ok =
        ok &&
        (cartStore.haveMembershipInCart ||
          (userStore.account && userStore.isActiveMember))
    }
    ok = ok && !cls.hasStarted

    if (ok) {
      return 'Join the waitlist'
    }

    if (siteConfig.copy?.classes?.learnMoreLabel) {
      return siteConfig.copy.classes.learnMoreLabel
    }
  }
  return 'Learn More'
}

const query = computed(() => route.query)

const filterKeys = Object.keys(classStore.filters)

const haveActiveFilters = computed(
  () => Object.values(classStore.filters).filter((v) => v !== null).length > 0
)

const allTypesTitle = computed(() =>
  route.meta.division === 'workshop' ? 'All Workshops' : 'All Classes'
)
const pageTitle = computed(() =>
  route.meta.division === 'workshop' ? 'Workshops' : siteConfig.copy?.classes?.pageTitle || 'Classes'
)
const bannerName = computed(() =>
  route.meta.division === 'workshop' ? 'workshops' : 'classes'
)

const getClassLevelSummary = (cls) => {
  let summary = ''
  if (cls.classLevel) {
    summary += cls.classLevel
  }
  if (cls.ages) {
    if (cls.classLevel) {
      summary += ', '
    }
    summary += cls.ages
  }
  return summary
}

const resetFilters = (e) => {
  classStore.resetFilters()
  selectedType.value = allTypesTitle.value
  filterDropdowns.value.forEach((dropdown) => {
    dropdown.selectedItem = null
  })
}

const dateRange = reactive({ startDate: null, endDate: null })

const clearDates = () => {
  dateRange.startDate = null
  dateRange.endDate = null
  classStore.filters.dateRange = null
}

const applyDates = () => {
  if (dateRange.startDate && dateRange.endDate) {
    classStore.filters.dateRange = `${dateRange.startDate}_${dateRange.endDate}`
  } else if (dateRange.startDate) {
    classStore.filters.dateRange = `${dateRange.startDate}`
  }
}

const filterValues = computed(() =>
  filterKeys.map((key) => classStore.filters[key]).filter((val) => val !== null)
)

const filterSummary = computed(() => {
  const zeroOrMany =
    classStore.filteredList.length == 0 || classStore.filteredList.length > 1
  const matchString = zeroOrMany ? 'matches' : 'match'

  const re = /^20[0-9]{2}-[0-9]{2}-[0-9]{2}_/

  let summary = `${classStore.filteredList.length} ${matchString} for `
  summary += filterValues.value
    .map((val) => {
      if (Array.isArray(val)) {
        return val.map(v => {
          if (v.match(re)) {
            const parts = v.split('_')
            return `<strong>${parts[0]} – ${parts[1]}</strong>`
          }
          return `<strong>${v}</strong>`
        }).join(', ')
      } else {
        if (val.match(re)) {
          const parts = val.split('_')
          return `<strong>${parts[0]} – ${parts[1]}</strong>`
        }
        return `<strong>${val}</strong>`
      }
    })
    .join(', ')
  return summary
})

const removeFilterParams = (params) => {
  const newParams = { ...params }
  filterKeys.forEach((key) => {
    if (newParams[key]) {
      delete newParams[key]
    }
  })
  return newParams
}

const getFilterParams = (params) => {
  const oldParams = { ...params }
  const newParams = {}
  filterKeys.forEach((key) => {
    if (oldParams[key]) {
      newParams[key] = oldParams[key]
    }
  })
  return newParams
}

const parseFiltersFromRouteQuery = (queryString) => {
  const filters = {}

  const parseMultipleValues = (key) => {
    if (Array.isArray(queryString[key])) {
      return queryString[key].map(v => decodeURIComponent(v.trim()))
    } else if (typeof queryString[key] === 'string') {
      return [decodeURIComponent(queryString[key].trim())]
    }
    return null
  }

  Object.keys(queryString).forEach((key) => {
    filters[key] = parseMultipleValues(key)
  })

  if (queryString.category) {
    filters.category = parseMultipleValues('category')
    if (!sidebarShowsMediums && !sidebarShowsPrograms) {
      selectedType.value = filters.category
    }
  }

  if (queryString.program) {
    filters.program = parseMultipleValues('program')
    if (sidebarShowsPrograms) {
      selectedType.value = filters.program
    }
  }

  if (queryString.type) {
    filters.type = parseMultipleValues('type')
  }

  if (queryString.dateRange) {
    filters.dateRange = queryString.dateRange.trim()
  }

  if (queryString.ageRange) {
    filters.ageRange = queryString.ageRange.trim()
  }

  if (queryString.medium) {
    filters.medium = parseMultipleValues('medium')
    if (sidebarShowsMediums) {
      selectedType.value = filters.medium
    }
  }

  return filters
}
const queryParams = computed(() => classStore.queryParams)

watch([queryParams], () => {
  const filtersFromQuery = getFilterParams(query.value)
  // return // XXX why was this added?

  if (route.path === '/classes' || route.path === '/workshops') {
    if (
      Object.keys(classStore.queryParams).length === 0 &&
      Object.keys(filtersFromQuery).length > 0
    ) {
      const newParams = removeFilterParams(query.value)

      const filterParams = getFilterParams(query.value)
      if (Object.keys(filterParams).length > 0) {
        router.push({
          path: route.path,
          query: newParams,
        })
      }
    } else if (Object.keys(classStore.queryParams).length) {
      if (!isEqual({ ...classStore.queryParams }, filtersFromQuery)) {
        const newParams = removeFilterParams(query.value)

        router.push({
          path: route.path,
          query: { ...newParams, ...classStore.queryParams },
        })
      }
    }
  }
})

const { prefixTitle } = useTitle()
watch([query], () => {
  const filters = parseFiltersFromRouteQuery(query.value)
  if (isEqual(filters, classStore.queryParams)) {
  } else {
    if (Object.keys(filters).length === 0) {
      classStore.resetFilters()
    } else {
      classStore.applyFilters(filters)
    }
  }

  prefixTitle(Object.values(filters).join(', '));
})

const routeMeta = computed(() => route.meta)
watch(routeMeta, (newVal, oldVal) => {
  if (newVal.division != oldVal.division) {
    classStore.hiddenFilters.division = route.meta.division
    classStore.resetFilters()
    selectedType.value = allTypesTitle.value
  }
})

onMounted(() => {
  if (route.meta.division) {
    classStore.hiddenFilters.division = route.meta.division
  }

  const filterObject = parseFiltersFromRouteQuery(query.value)
  const filtersFromQuery = getFilterParams(query.value)

  if (isEqual(filtersFromQuery, classStore.queryParams)) {
  } else {
    classStore.applyFilters(filterObject)
  }
})

const selectedType = ref(route.query.type ? '' : allTypesTitle.value)

const sidebarShowsMediums = siteConfig.features.classFilters?.sidebarFilter === 'mediums'
const sidebarShowsPrograms = siteConfig.features.classFilters?.sidebarFilter === 'programs'

const classTypes = computed(() => {
  let types, selected
  if (sidebarShowsMediums) {
    types = classStore.mediums
    selected = classStore.filters.medium
  } else if (sidebarShowsPrograms) {
    types = classStore.programsWithOpenClasses
    selected = classStore.filters.program
  } else {
    types = classStore.categories
    if (siteConfig.features.categoryOrder) {
      // XXX TODO use weights instead of particular order so updates aren't needed when new categories are added.
      const order = siteConfig.features.categoryOrder
      types = types.sort((a, b) => {
        // console.warn('order', a, order.indexOf(a), b, order.indexOf(b))
        if (order.indexOf(a) > order.indexOf(b)) return 1
        if (order.indexOf(a) < order.indexOf(b)) return -1
        return 0
      })
    }

    selected = classStore.filters.category
  }
  
  if (!selected) {
    selected = allTypesTitle.value
  }
  return {
    options: [allTypesTitle.value, ...types],
    // update: (option) => updateFilter(sidebarShowsMediums ? 'medium' : (sidebarShowsPrograms ? 'program' : 'category'), option),
    selectedItem: selected,
  }
})

const updateClassType = (type) => {
  selectedType.value = type
  updateFilter(
    sidebarShowsMediums
      ? 'medium'
      : sidebarShowsPrograms
      ? 'program'
      : 'category',
    type
  )
}

const updateFilter = (filter, value) => {
  if (
    value.toLowerCase().includes('any ') ||
    value.toLowerCase().includes(allTypesTitle.value.toLowerCase()) ||
    value.toLowerCase().includes('on-site & online')
  ) {
    classStore.filters[filter] = null
  } else {
    classStore.filters[filter] = value
  }
}

const categoriesDropdown = computed(() => {
  return {
    options: [
      siteConfig.features.classFilters?.dropdownLabels?.categories ||
        'Any program category',
      ...classStore.categories,
    ],
    update: (option) => updateFilter('category', option),
    selectedItem: classStore.filters.category,
  }
})

const semestersDropdown = computed(() => {
  return {
    options: [
      siteConfig.features.classFilters?.dropdownLabels?.semesters ||
        'Any semester',
      ...classStore.semesters,
    ],
    update: (option) => updateFilter('semester', option),
    selectedItem: classStore.filters.semester,
  }
})

const mediumsDropdown = computed(() => {
  return {
    options: [
      siteConfig.features.classFilters?.dropdownLabels?.mediums || 'Any medium',
      ...classStore.mediums,
    ],
    update: (option) => updateFilter('medium', option),
    selectedItem: classStore.filters.medium,
  }
})

const levelsDropdown = computed(() => {
  return {
    options: ['Any skill level', ...classStore.levels],
    update: (option) => updateFilter('level', option),
    selectedItem: classStore.filters.level,
  }
})

const availableDropdown = computed(() => {
  return {
    options: ['Any availability', 'Open Seats'],
    update: (option) => updateFilter('availability', option),
    selectedItem: classStore.filters.availability,
  }
})

const keywordsDropdown = computed(() => {
  return {
    options: ['Any keywords', ...classStore.keywords],
    update: (option) => updateFilter('keywords', option),
    selectedItem: classStore.filters.keywords,
  }
})


const sortByDropdown = computed(() => {
  return {
    options: classStore.sortByOptions,
    update: (option) => classStore.sortBy = option,
    selectedItem: classStore.sortBy,
    placeholder: 'Sort by',
    displayValue: () => `Sorted by ${classStore.sortByText}`,
    optionDisplay: (option) => option.text,
    optionValue: (option) => option.value,
  }
})

const daysDropdown = computed(() => {
  return {
    options: [
      'Any day',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ],
    update: (option) => updateFilter('day', option),
    selectedItem: classStore.filters.day,
  }
})

const sitesDropdown = computed(() => {
  return {
    options: [
      siteConfig.features.classFilters?.dropdownLabels?.sites || 'Any location',
      ...classStore.sites,
    ],
    update: (option) => updateFilter('site', option),
    selectedItem: classStore.filters.site,
  }
})

const locationsDropdown = computed(() => {
  return {
    options: [
      siteConfig.features.classFilters?.dropdownLabels?.locations ||
        'Any location',
      ...classStore.locations,
    ],
    update: (option) => updateFilter('location', option),
    selectedItem: classStore.filters.location,
  }
})

const locationTypesDropdown = computed(() => {
  return {
    options: ['On-site & Online', 'On-site', 'Online'],
    update: (option) => updateFilter('locationType', option),
    selectedItem: classStore.filters.locationType,
  }
})

const instructorsDropdown = computed(() => {
  return {
    options: [
      'Any instructor',
      ...instructorStore.instructorsWithOpenClasses.map((inst) => inst.fullName),
    ],
    update: (option) => updateFilter('instructor', option),
    selectedItem: classStore.filters.instructor,
  }
})

const ageRangesDropdown = computed(() => {
  return {
    options: [
      'Any age group',
      ...siteConfig.features.classFilters?.ageRangeFilters?.map((f) => f.name),
    ],
    update: (option) => updateFilter('ageRange', option),
    selectedItem: classStore.filters.ageRange,
  }
})

const filterDropdowns = computed(() => {
  let dropdowns = []

  if (siteConfig.features.classFilters?.sidebarFilter === 'mediums') {
    if (!siteConfig.features.classFilters?.disabled?.includes('categories')) {
      dropdowns.push(categoriesDropdown.value)
    }
  } else {
    if (!siteConfig.features.classFilters?.disabled?.includes('mediums')) {
      dropdowns.push(mediumsDropdown.value)
    }
  }

  // XXX Revisit this logic
  // If programs are on the side, show program categories as dropdown
  if (siteConfig.features.classFilters?.sidebarFilter === 'programs') {
    if (!siteConfig.features.classFilters?.disabled?.includes('categories')) {
      dropdowns.push(categoriesDropdown.value)
    }
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('semesters')) {
    dropdowns.push(semestersDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('levels')) {
    dropdowns.push(levelsDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('days')) {
    dropdowns.push(daysDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('instructors')) {
    dropdowns.push(instructorsDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('sites') && siteConfig.features.useSiteForLocation) {
    dropdowns.push(sitesDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('locations')) {
    dropdowns.push(locationsDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('locationTypes')) {
    dropdowns.push(locationTypesDropdown.value)
  }

  if (siteConfig.features.classFilters?.ageRangeFilters) {
    dropdowns.push(ageRangesDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('availability')) {
    dropdowns.push(availableDropdown.value)
  }

  if (siteConfig.features.classFilters?.keywords) {
    dropdowns.push(keywordsDropdown.value)
  }

  if (!siteConfig.features.classFilters?.disabled?.includes('sortClasses')) {
    dropdowns.push(sortByDropdown.value)
  }

  return dropdowns
})

// XXX TODO Pull into separate file

const spotsLeftText = (spots) => {
  if (spots === 1) {
    return `1 ${siteConfig.copy?.spotLeft || 'spot left'}`
  }
  return `${spots} ${siteConfig.copy?.spotsLeft || 'spots left'}`
}
const locationForClass = (cls) => {
  if (siteConfig.features.useLocationTypeForLocation) {
    return cls.locationType
  } else if (siteConfig.features.useSiteForLocation) {
    return cls.site
  }
  return cls.location
}

watch(() => userStore.loggedIn, (isLoggedIn) => {
  if (isLoggedIn && !siteConfig.features.disableBalanceCheck && !userStore.fetchedBalance) {
    userStore.fetchBalance()
  }
}, { immediate: true })
</script>

<template>
  <section class="storefront-classes">
    <div
      class="hero-banner"
      :style="`background-image: url(/sites/${siteConfig.key}/${siteConfig.assets.banners[bannerName] || siteConfig.assets.banners.default})`"
    >
      <div class="container">
        <h1>{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="container">
      <template v-if="classStore.loading">
        <LoadingHolder />
      </template>

      <div class="classes-grid" v-else>
        <div class="class-type">
          <h4 class="small-bold">
            {{ route.meta.division === 'workshop' ? 'Workshop' : 'Class' }}
            Types
          </h4>
          <ul class="types">
            <li
              v-for="(type, index) in classTypes.options"
              :key="index"
              v-html="type"
              @click="updateClassType(type)"
              :class="{ 'selected !font-bold': selectedType === type || (Array.isArray(selectedType) && selectedType.includes(type)) }"
              class="small-regular"
            ></li>


            <li
              class="small-regular mt-6 flex justify-baseline"
              v-if="siteConfig.key === 'krasl'"
            >
              <AcademicCapIcon class="w-5 h-5 inline-flex mr-1" />
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScQ9_sRWbA6j3K9qpKZVlFw8Mk3T9mb4nHPY5KxUhlr_c_JdA/viewform"
              >Scholarships</a>
            </li>

            <li
              class="small-regular mt-6 flex justify-baseline"
              v-if="siteConfig.key === 'potters' && userStore.isActiveMember"
            >
              <BookOpenIcon class="w-5 h-5 inline-flex mr-1" />
              <a
                target="_blank"
                href="/sites/potters/2023_TPS_Member_Handbook.pdf"
              >Member Handbook</a>
            </li>

            <li
              class="small-regular mt-6 flex justify-baseline"
              v-if="siteConfig.key === 'pittsburgh'"
            >
              <FireIcon class="w-5 h-5 inline-flex mr-1" />
              <a href="https://www.pittsburghglasscenter.org/plan-a-visit/#wimin" target="_blank">NEW! Walk-In + Make-It-Now</a>
            </li>
            <li
              class="small-regular mt-6 flex justify-baseline"
              v-if="siteConfig.key === 'pittsburgh'"
            >
              <AcademicCapIcon class="w-5 h-5 inline-flex mr-1" />
              <a href="https://www.pittsburghglasscenter.org/apply-scholarships">Scholarships</a>
            </li>
            <li
              class="small-regular mt-6 flex justify-baseline"
              v-if="siteConfig.key === 'pittsburgh'"
            >
              <GiftIcon class="w-5 h-5 inline-flex mr-1" />
              <a href="https://pghglasscenter.square.site/gift-cards">Gift Certificates</a>
            </li>
            <li
              class="small-regular mt-6 flex justify-baseline"
              v-if="siteConfig.key === 'pittsburgh'"
            >
              <ClipboardDocumentListIcon class="w-5 h-5 inline-flex mr-1" />
              <a href="https://www.pittsburghglasscenter.org/class-policies">Class Policies</a>
            </li>
          </ul>

          <h4 class="small-bold mt-8" v-if="siteConfig.key === 'chesapeake'">
            Open Studio Access
          </h4>
          <ul class="types" v-if="siteConfig.key === 'chesapeake'">
            <li>
              <router-link to="/classes?medium=MakerSpace+Open+Studio">
                MakerSpace
              </router-link>
            </li>
            <li>
              <router-link to="/classes?medium=Ceramics+Open+Studio"> Ceramics </router-link>
            </li>
          </ul>

          <DateFilter :dateRange="dateRange" :clearDates="clearDates" :applyDates="applyDates" />
        </div>
        <div class="classes-view">
          <div
            class="store-warning small-regular mb-6 !items-start"
            v-if="
              siteConfig.features.showNewSystemPasswordResetAnnouncement &&
                !userStore.loggedIn
            "
          >
            <InformationCircleIcon class="warning-icon" />
            <p class="warning-text">
              If this is your first time using our new system, please
              <router-link
                :to="{ name: 'Forgot Password' }"
                class="text-accent-1"
              >
                reset your password
              </router-link>
              to activate your account.
            </p>
          </div>
          <div
            class="store-warning small-regular mb-6 !items-start"
            v-if="
              siteConfig.key === 'district-clay' && !filterValues?.some((element) => {
                if (Array.isArray(element)) {
                  return element.some(v => v.toLowerCase().includes('workshop'))
                } else {
                  return element?.toLowerCase()?.includes('workshop')
                }})"
          >
            <InformationCircleIcon class="warning-icon" />
            <p class="warning-text">
              Our classes are filled for this session. DCC will contact those on the waitlist if/when spaces becomes available for 
              registration.  Registration will be first come and spaces fill quickly.  To add your name to the waitlist click the 
              <strong>Join the waitlist</strong> button on the class page.
            </p>
          </div>

          <div
            class="store-warning small-regular mb-6"
            v-if="userStore.balance > 0"
          >
            <InformationCircleIcon class="warning-icon" />

            <p class="warning-text">
              You have a balance of ${{ userStore.balance.toFixed(2) }} due on your account.
              <button class="warning-link" @click.prevent="payBalance">
                Pay Now
              </button>
            </p>
          </div>

          <!-- <BaseDropdown :dropdown="classTypes" @updateSelected="updateClassType" class="class-type-drop" /> -->

          <Listbox
            v-model="selectedType"
            @update:modelValue="updateClassType"
            v-slot="{ open }"
            class="base-listbox py-4 md:hidden"
            as="div"
          >
            <div class="listbox-overlay" v-if="open"></div>
            <div class="relative">
              <ListboxButton
                class="listbox-button"
                :class="{ 'filter-selected': selectedType && (selectedType !== classTypes.options[0]) }"
              >
                <span class="block truncate">{{ Array.isArray(selectedType) ? selectedType.join(', ') : (selectedType || classTypes.options[0]) }}</span>
                <span class="selector-icon">
                  <Icon icon="heroicons-outline:selector" class="h-5 w-5 text-secondary-text" />
                </span>
              </ListboxButton>
              <transition name="fade-fast">
                <ListboxOptions class="listbox-options" static v-show="open">
                  <ListboxOption
                    v-slot="{ active }"
                    v-for="option, index in classTypes.options"
                    :value="option"
                    as="template"
                  >
                    <li
                      class="listbox-option small-regular"
                      :class="[
                        { 'bg-gray-50': active },
                        { 'font-bold text-accent-1': Array.isArray(selectedType) ? selectedType.includes(option) : (selectedType === option) }
                        ]"
                      v-html="option"
                    ></li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>


          <ClassFilters 
            :siteConfig="siteConfig" 
            :filterDropdowns="filterDropdowns" 
            :haveActiveFilters="haveActiveFilters" 
            :filterSummary="filterSummary" 
            :resetFilters="resetFilters" 
            :filterValues="filterValues" 
            :dateRange="dateRange" 
            :clearDates="clearDates" 
            :applyDates="applyDates"
          />


          <div class="classes-wrapper two-columns-layout">
            <div
              class="class-box"
              v-for="(cls, index) in classStore.filteredList"
              :key="index"
            >
              <div class="class-image">
                <router-link
                  :to="{
                    name: 'Class Details',
                    params: { classId: cls.catalogId },
                  }"
                >
                  <img v-lazy="cls.imageUrl" />
                  <div
                    class="left-spots-overlay xlarge-text"
                    v-if="
                      !cls.hasStarted &&
                        !cls.daySales &&
                        siteConfig.features.showSpotsLeftThreshold &&
                        cls.seatsAvailable <=
                        siteConfig.features.showSpotsLeftThreshold &&
                        cls.seatsAvailable > 0
                    "
                  >
                    {{ spotsLeftText(cls.seatsAvailable) }}
                  </div>
                  <div class="waitlist-overlay" v-if="cls.classIsFull && !siteConfig.features?.hideClassFullMessage">
                    <h4 class="xlarge-text">Class Full</h4>
                    <span
                      class="small-regular"
                      v-if="
                        cls.waitList && !cls.isPastCutOffDate && !cls.hasStarted
                      "
                    >waitlist only</span>
                  </div>
                  <div
                    class="image-overlay"
                    v-if="!siteConfig.features?.hideClassImageOverlay"
                  >
                    <h5
                      class="large-bold"
                      v-html="
                        siteConfig.features.preferClassMediums
                          ? cls.medium1 || cls.medium2 || cls.medium
                          : cls.medium || cls.medium1 || cls.medium2
                      "
                    ></h5>
                    <span
                      v-if="!siteConfig.features.hideLocationTypeInOverlay"
                      class="extra-small"
                      v-html="
                        siteConfig.features.showSiteInOverlay
                          ? cls.site
                          : cls.locationType
                      "
                    ></span>
                  </div>
                </router-link>
              </div>

              <div class="class-info">
                <a
                  v-if="cls.udef1 && cls.udef1.includes('http')"
                  :href="cls.udef1"
                >
                  <h2
                    class="class-name xlarge-text xl:heading-3 mb-2"
                    v-html="cls.shortTitle"
                  ></h2></a>
                <router-link
                  v-else
                  :to="{
                    name: 'Class Details',
                    params: { classId: cls.catalogId },
                  }"
                >
                  <h2
                    class="class-name xlarge-text xl:heading-3 mb-2"
                    v-html="cls.shortTitle"
                  ></h2>
                </router-link>

                <div class="wrapper">
                  <p
                    class="class-code"
                    v-if="siteConfig.features.showClassCodes"
                  >
                    <Icon icon="ant-design:barcode-outlined" class="icon" />
                    <span v-html="cls.code"></span>
                  </p>
                  <p class="class-instructor" v-if="cls.instructorSummary">
                    <Icon icon="ant-design:user-outlined" class="icon" />
                    <span v-html="cls.instructorSummary"></span>
                  </p>
                  <p class="class-schedule" v-if="!siteConfig.features.hideClassDate">
                    <Icon icon="ci:calendar" class="icon" />
                    <span v-html="cls.dateSummary"></span>
                  </p>
                  <p
                    class="class-schedule"
                    v-if="!siteConfig.features.hideClassTime"
                  >
                    <Icon icon="ci:clock" class="icon" />
                    <span v-html="cls.timeSummary"></span>
                  </p>
                  <p
                    class="class-code"
                    v-if="
                      siteConfig.features.showProgramNameInSummary &&
                        classStore.programsById[cls.eventId]
                    "
                  >
                    <Icon icon="ant-design:hourglass-outlined" class="icon" />
                    <span
                      v-html="classStore.programsById[cls.eventId].title"
                    ></span>
                  </p>
                  <p
                    class="class-location"
                    v-if="!siteConfig.features.hideClassLocation"
                  >
                    <Icon icon="carbon:location" class="icon" />
                    <span v-html="locationForClass(cls)"></span>
                  </p>
                  <p class="class-level">
                    <Icon icon="carbon:meter-alt" class="icon" />
                    <span v-html="getClassLevelSummary(cls)"></span>
                  </p>

                  <div class="price-wrapper" :class="{'xl:absolute bottom-0 left-0': siteConfig.key != 'pittsburgh'}">
                    <div v-if="cls.canAddEntireSeries">
                      <div
                        v-if="siteConfig.features.emphasizeMemberPrice"
                        class="class-price xl:!relative"
                      >
                        <h3 class="main-price heading-3">
                          &dollar;{{ cls.memberCostFmt }}
                        </h3>
                        <h4
                          class="members-price body-bold"
                          v-if="cls.nonMemberCostFmt != cls.memberCostFmt"
                        >
                          &dollar;{{ cls.nonMemberCostFmt }}
                          <span class="small-regular">(non-members)</span>
                        </h4>
                        <h4
                          class="members-price xl:heading-4 body-bold"
                          v-if="cls.fee && cls.fee != 0"
                        >
                          + &dollar;{{ cls.fee.toFixed(2) }}
                          <span class="small-regular lg:body-regular">({{
                            cls.feeType || 'supply fee'
                          }})</span>
                        </h4>
                      </div>
                      <div v-else class="class-price">
                        <h3 class="main-price heading-3" v-if="!siteConfig.features.normalizePricing">
                          &dollar;{{ cls.nonMemberCostFmt }}
                        </h3>
                        <h4 v-else
                          class="members-price body-bold"
                        >
                          &dollar;{{ cls.nonMemberCostFmt }}
                          <span class="small-regular">(non-members)</span>
                        </h4>
                        <h4
                          class="members-price body-bold"
                          v-if="cls.nonMemberCostFmt != cls.memberCostFmt"
                        >
                          &dollar;{{ cls.memberCostFmt }}
                          <span class="small-regular">(members)</span>
                        </h4>
                        <h4
                          class="members-price xl:heading-4 body-bold"
                          v-if="cls.fee && cls.fee != 0"
                        >
                          + &dollar;{{ cls.fee.toFixed(2) }}
                          <span class="small-regular lg:body-regular">({{
                            cls.feeType || 'supply fee'
                          }})</span>
                        </h4>
                      </div>
                    </div>

                    <div
                      v-if="
                        cls.daySales &&
                          siteConfig.features.showSessionRateInClassList &&
                          siteConfig.features.emphasizeMemberPrice
                      "
                      class="class-price !mt-1"
                    >
                      <h3 class="main-price" :class="{'heading-3': !cls.canAddEntireSeries, 'body-bold': cls.canAddEntireSeries}">
                        &dollar;{{ cls.sessionCostMem.toFixed(2) }} / session
                      </h3>
                      <h4
                        class="members-price body-bold text-sm"
                        v-if="cls.sessionCostMem != cls.sessionCostNon"
                      >
                        &dollar;{{ cls.sessionCostNon.toFixed(2) }}
                        <span class="small-regular">(non-members)</span>
                      </h4>
                      <h4
                        class="members-price xl:heading-4 body-bold"
                        v-if="cls.fee && cls.fee != 0"
                      >
                        + &dollar;{{ cls.feePerSession.toFixed(2) }}
                        <span class="small-regular lg:body-regular">({{
                          cls.feeType || 'supply fee'
                        }})</span>
                      </h4>
                    </div>
                    <div
                      v-if="
                        cls.daySales &&
                          siteConfig.features.showSessionRateInClassList &&
                          !siteConfig.features.emphasizeMemberPrice
                      "
                      class="class-price !mt-1"
                    >
                      <h3 class="main-price" :class="{'heading-3': !cls.canAddEntireSeries, 'body-bold': cls.canAddEntireSeries}">
                        &dollar;{{ cls.sessionCostNon }} / session
                      </h3>
                      <h4
                        class="members-price body-bold text-sm"
                        v-if="cls.sessionCostNon != cls.sessionCostMem"
                      >
                        &dollar;{{ cls.sessionCostMem }}
                        <span class="small-regular">(members)</span>
                      </h4>
                      <h4
                        class="members-price xl:heading-4 body-bold"
                        v-if="cls.fee && cls.fee != 0"
                      >
                        + &dollar;{{ cls.feePerSession.toFixed(2) }}
                        <span class="small-regular lg:body-regular">({{
                          cls.feeType || 'supply fee'
                        }})</span>
                      </h4>
                    </div>
                  </div>

                  <a
                    v-if="cls.udef1 && cls.udef1.includes('http')"
                    :href="cls.udef1"
                    class="btn-accent-1 btn-register xl:large-regular"
                  >
                    Register
                  </a>
                  <router-link
                    v-else
                    :to="{
                      name: 'Class Details',
                      params: { classId: cls.catalogId },
                    }"
                    class="btn-accent-1 btn-register xl:large-regular"
                  >
                    {{ learnMoreLabel(cls) }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="classStore.filteredList.length === 0 && siteConfig.key === 'snowfarm'" class="no-class-note">
            <p>Our workshop season is now over! Thank you to everyone who brought joy and creativity to the Snow Farm community.</p>
            <br>
            <p><b>Next up:</b> the <a href="https://www.snowfarm.org/support/seconds-sale"> Seconds Sale </a> for three weekends in November. Come and find a treasure! </p>
            <br>
            <p>Next year's workshop schedule will be posted on this page in mid-December. Please check back or sign up for our <a href="https://www.snowfarm.org/newsletter-signup">mailing list</a> to get a reminder. </p>
          </div>

          <!-- <BasePagination /> -->
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@import url('./index.pcss');
</style>
